import React, { useState } from 'react';
import { 
  ArrowLeft, 
  TrendingUp, 
  DollarSign, 
  Clock, 
  PieChart, 
  CheckCircle2, 
  AlertCircle, 
  Zap, 
  Calendar, 
  ChevronRight, 
  BarChart3, 
  Layers, 
  Target, 
  ShieldCheck, 
  Percent, 
  ArrowUpRight,
  ClipboardList,
  Receipt as ReceiptIcon,
  Wrench,
  Package
} from 'lucide-react';
import { formatCurrencyBRL } from '../../../utils/formatUtils';
import { ErpProposalItem, WorkOrder, Receipt } from '../types';
import ScreenTourButton from '../../../components/tour/ScreenTourButton';

interface ExecutiveCockpitDashboardProps {
  monthlyFixedCosts: number;
  proLaboreGoal: number;
  currentVhbRate: number;
  monthlyCapacityHours: number;
  proposals?: ErpProposalItem[];
  workOrders?: WorkOrder[];
  receipts?: Receipt[];
  onBack: () => void;
  onNavigateToCostCenter?: () => void;
  onNavigateToQuotes?: () => void;
  onNavigateToCalendar?: () => void;
  onNavigateToWorkOrders?: () => void;
  onNavigateToReceipts?: () => void;
}

export default function ExecutiveCockpitDashboard({
  monthlyFixedCosts = 2400,
  proLaboreGoal = 5000,
  currentVhbRate = 85,
  monthlyCapacityHours = 140,
  proposals = [],
  workOrders = [],
  receipts = [],
  onBack,
  onNavigateToCostCenter,
  onNavigateToQuotes,
  onNavigateToCalendar,
  onNavigateToWorkOrders,
  onNavigateToReceipts
}: ExecutiveCockpitDashboardProps) {
  const [selectedPeriod, setSelectedPeriod] = useState<'current_month' | 'last_30_days'>('current_month');

  // Filtro de data dinâmico
  const isWithinPeriod = (dateStr?: string) => {
    if (!dateStr) return true;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return true;
    const now = new Date();
    if (selectedPeriod === 'current_month') {
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    }
    // last_30_days
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return date.getTime() >= thirtyDaysAgo;
  };

  // Cálculos do Ponto de Equilíbrio (Break-Even)
  const breakEvenTarget = monthlyFixedCosts + proLaboreGoal;
  const workingDaysInMonth = 22;
  const dailyBreakEvenTarget = breakEvenTarget / workingDaysInMonth;

  // 1. Propostas aprovadas no período
  const approvedProposals = proposals.filter(p => {
    const isApproved = (
      p.stage === 'aprovado' || 
      p.stage === 'approved' || 
      p.stage === 'em_andamento' || 
      p.stage === 'in_progress' || 
      p.stage === 'concluido' || 
      p.stage === 'completed' || 
      p.stage === 'paid'
    );
    return isApproved && isWithinPeriod(p.createdAt);
  });

  // 2. Ordens de Serviço no período (concluídas ou em andamento)
  const activeWorkOrders = workOrders.filter(wo => {
    const isValidStatus = wo.status === 'completed' || wo.status === 'in_progress';
    return isValidStatus && isWithinPeriod(wo.createdAt);
  });

  // Faturamento Propostas
  const quotesRevenue = approvedProposals.reduce((sum, p) => sum + (p.finalPrice || 0), 0);
  const quotesMaterials = approvedProposals.reduce((sum, p) => sum + (p.materialsCost || 0), 0);
  const quotesLabor = Math.max(0, quotesRevenue - quotesMaterials);

  // Faturamento Ordens de Serviço
  const woRevenue = activeWorkOrders.reduce((sum, wo) => sum + (wo.finalPrice || 0), 0);
  const woMaterials = activeWorkOrders.reduce((sum, wo) => sum + (wo.totalMaterials || 0), 0);
  const woLabor = activeWorkOrders.reduce((sum, wo) => sum + (wo.totalLabor || 0), 0);

  // Faturamento Consolidado
  const grossBilledMonth = Math.max(quotesRevenue, woRevenue) > 0 ? (quotesRevenue + woRevenue) : (quotesRevenue || woRevenue || 0);
  const totalMaterialsSpent = quotesMaterials + woMaterials;
  const totalLaborBilled = grossBilledMonth > totalMaterialsSpent ? (grossBilledMonth - totalMaterialsSpent) : (quotesLabor + woLabor);

  // Lucro Líquido Real e Margem
  const netProfitMonth = grossBilledMonth - totalMaterialsSpent - monthlyFixedCosts;
  const netMarginPercent = grossBilledMonth > 0 ? (netProfitMonth / grossBilledMonth) * 100 : 0;
  
  // Progresso do Ponto de Equilíbrio
  const breakEvenProgressPct = breakEvenTarget > 0 
    ? Math.min(200, Math.round((grossBilledMonth / breakEvenTarget) * 100)) 
    : 0;
  const isBreakEvenAchieved = grossBilledMonth >= breakEvenTarget && breakEvenTarget > 0;

  // Horas Faturadas vs. Ociosidade
  const billedHours = currentVhbRate > 0 
    ? Math.max(0, Math.round(totalLaborBilled / currentVhbRate)) 
    : 0;
  const utilizationRate = monthlyCapacityHours > 0 
    ? Math.min(100, Math.round((billedHours / monthlyCapacityHours) * 100)) 
    : 0;
  const idleHours = Math.max(0, monthlyCapacityHours - billedHours);
  const idlePotentialRevenue = idleHours * currentVhbRate;

  // Ticket Médio
  const totalClosedCount = approvedProposals.length + activeWorkOrders.length;
  const averageTicket = totalClosedCount > 0 ? grossBilledMonth / totalClosedCount : 0;

  // Percentuais Mão de Obra vs Materiais
  const laborPct = grossBilledMonth > 0 ? Math.round((totalLaborBilled / grossBilledMonth) * 100) : 100;
  const materialsPct = grossBilledMonth > 0 ? Math.round((totalMaterialsSpent / grossBilledMonth) * 100) : 0;

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 pb-20 font-sans">
      {/* Header Sticky Slim (56px) - Padrão R-MAX Mobile */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-zinc-200/80 px-4 h-14 flex items-center justify-between shadow-2xs">
        <div className="flex items-center gap-2.5 min-w-0">
          <button
            type="button"
            onClick={onBack}
            className="p-2 -ml-1 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-xl transition-all active:scale-95 cursor-pointer shrink-0"
            title="Voltar ao Painel"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-sm sm:text-base font-black text-zinc-900 truncate tracking-tight">
              Painel Financeiro & Lucro
            </h1>
            <p className="text-[11px] text-zinc-500 truncate font-medium">
              Faturamento, custos e seu salário no mês
            </p>
          </div>
        </div>

        {/* Seletor de Período & Botão de Ajuda */}
        <div className="flex items-center gap-2 shrink-0">
          <ScreenTourButton screenKey="cockpit" />
          <div className="flex bg-zinc-100 p-1 rounded-2xl text-xs font-bold shrink-0">
            <button
              type="button"
              onClick={() => setSelectedPeriod('current_month')}
              className={`px-3 py-1.5 rounded-xl transition-all cursor-pointer ${
                selectedPeriod === 'current_month' ? 'bg-brand text-white font-black shadow-xs' : 'text-zinc-600 hover:text-zinc-900'
              }`}
            >
              Mês Atual
            </button>
            <button
              type="button"
              onClick={() => setSelectedPeriod('last_30_days')}
              className={`px-3 py-1.5 rounded-xl transition-all cursor-pointer ${
                selectedPeriod === 'last_30_days' ? 'bg-brand text-white font-black shadow-xs' : 'text-zinc-600 hover:text-zinc-900'
              }`}
            >
              Últimos 30 Dias
            </button>
          </div>
        </div>
      </header>

      {/* Conteúdo Principal */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        
        {/* 1. Hero Card: Meta Financeira (Cobrir Custos + Seu Salário) */}
        <div className="bg-white text-zinc-900 rounded-3xl p-5 sm:p-6 shadow-xs border border-zinc-200/80 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <span className="text-[10px] font-black text-emerald-700 uppercase tracking-widest bg-emerald-50 border border-emerald-200/80 px-3 py-1 rounded-full">
                Meta do Mês (Custos + Seu Salário)
              </span>
              <h2 className="text-2xl sm:text-3xl font-black text-zinc-900 mt-2 tracking-tight">
                {formatCurrencyBRL(grossBilledMonth)} <span className="text-sm text-zinc-500 font-bold">faturados no período</span>
              </h2>
              <p className="text-xs text-zinc-500 font-medium mt-1">
                Custos Fixos ({formatCurrencyBRL(monthlyFixedCosts)}) + Seu Salário ({formatCurrencyBRL(proLaboreGoal)}) = <strong className="text-zinc-900 font-bold">Meta: {formatCurrencyBRL(breakEvenTarget)}</strong>
              </p>
            </div>

            <div className="text-left sm:text-right">
              <span className={`text-lg sm:text-xl font-black px-4 py-2 rounded-2xl border inline-block ${
                isBreakEvenAchieved ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-amber-50 text-amber-800 border-amber-200/80'
              }`}>
                {breakEvenProgressPct}% da Meta
              </span>
            </div>
          </div>

          {/* Barra de Progresso da Meta */}
          <div className="space-y-1.5 pt-1">
            <div className="h-3 w-full bg-zinc-100 rounded-full overflow-hidden p-0.5 border border-zinc-200">
              <div 
                className={`h-full rounded-full transition-all duration-1000 ${
                  isBreakEvenAchieved ? 'bg-brand shadow-xs' : 'bg-amber-500'
                }`}
                style={{ width: `${Math.min(100, breakEvenProgressPct)}%` }}
              />
            </div>
            <div className="flex justify-between text-[10px] text-zinc-500 font-medium flex-wrap gap-1">
              <span>R$ 0,00</span>
              <span className="text-zinc-700 font-bold">Meta de Faturamento: {formatCurrencyBRL(breakEvenTarget)}</span>
              <span className="text-emerald-700 font-bold">Lucro Acima da Meta</span>
            </div>
          </div>

          <div className="p-3 bg-zinc-50 rounded-2xl border border-zinc-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
            <span className="text-zinc-600 font-medium">
              Meta por Dia Útil: <strong className="text-zinc-900 font-bold">{formatCurrencyBRL(dailyBreakEvenTarget)} / dia</strong> ({workingDaysInMonth} dias úteis no mês)
            </span>
            {isBreakEvenAchieved ? (
              <span className="text-emerald-700 font-black flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                Custos e Salário Pagos! Lucro Protegido
              </span>
            ) : (
              <span className="text-amber-700 font-bold">
                Faltam {formatCurrencyBRL(Math.max(0, breakEvenTarget - grossBilledMonth))} para atingir a meta
              </span>
            )}
          </div>
        </div>

        {/* 2. Grid de 3 Cards Estratégicos */}
        <div className="grid sm:grid-cols-3 gap-3.5 text-xs">
          {/* Card A: Capacidade Produtiva & Ociosidade */}
          <div className="bg-white p-5 rounded-3xl border border-zinc-100 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-zinc-400 font-black uppercase tracking-widest">Tempo & Agenda Ocupada</span>
              <div className="w-7 h-7 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-black">
                <Clock className="h-3.5 w-3.5" />
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-black text-zinc-900">{utilizationRate}%</h3>
              <p className="text-[10px] text-zinc-500 font-medium">{billedHours}h faturadas de {monthlyCapacityHours}h disponíveis</p>
            </div>
            <div className="pt-2 border-t border-zinc-100">
              <span className="text-[10px] text-zinc-400 block font-bold">Potencial nas Horas Livres:</span>
              <p className="text-xs font-black text-brand">{formatCurrencyBRL(idlePotentialRevenue)}</p>
              <span className="text-[10px] text-zinc-500 font-medium">({idleHours}h livres para faturar)</span>
            </div>
          </div>

          {/* Card B: Lucro Líquido Real em Caixa */}
          <div className="bg-white p-5 rounded-3xl border border-zinc-100 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-zinc-400 font-black uppercase tracking-widest">Lucro Livre do Mês</span>
              <div className="w-7 h-7 rounded-xl bg-emerald-50 text-brand flex items-center justify-center font-black">
                <DollarSign className="h-3.5 w-3.5" />
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-black text-brand">{formatCurrencyBRL(netProfitMonth)}</h3>
              <p className="text-[10px] text-brand font-bold">Margem de Lucro: {netMarginPercent.toFixed(1)}%</p>
            </div>
            <div className="pt-2 border-t border-zinc-100">
              <span className="text-[10px] text-zinc-400 block font-bold">Seu Salário / Retirada:</span>
              <p className="text-xs font-black text-zinc-900">{formatCurrencyBRL(proLaboreGoal)}</p>
              <span className="text-[10px] text-zinc-500 font-medium">Retirada mensal garantida</span>
            </div>
          </div>

          {/* Card C: Ticket Médio & Conversão */}
          <div className="bg-white p-5 rounded-3xl border border-zinc-100 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-zinc-400 font-black uppercase tracking-widest">Atendimentos & Vendas</span>
              <div className="w-7 h-7 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center font-black">
                <Target className="h-3.5 w-3.5" />
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-black text-zinc-900">{formatCurrencyBRL(averageTicket)}</h3>
              <p className="text-[10px] text-zinc-500 font-medium">Valor médio por serviço fechado</p>
            </div>
            <div className="pt-2 border-t border-zinc-100">
              <span className="text-[10px] text-zinc-400 block font-bold">Total de Serviços Fechados:</span>
              <p className="text-xs font-black text-purple-700">{totalClosedCount} serviços</p>
              <span className="text-[10px] text-zinc-500 font-medium">Neste período</span>
            </div>
          </div>
        </div>

        {/* 3. Composição de Receita: Mão de Obra vs. Materiais */}
        <div className="bg-white rounded-3xl p-5 sm:p-6 border border-zinc-100 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
            <div>
              <h3 className="text-xs font-black text-zinc-900 uppercase tracking-wider">
                De Onde Vem o Seu Faturamento
              </h3>
              <p className="text-[11px] text-zinc-500 font-medium">
                Veja quanto veio da sua mão de obra (serviço) versus materiais e peças repassadas.
              </p>
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500 bg-zinc-100 px-2.5 py-0.5 rounded-full">
              Composição Real
            </span>
          </div>

          <div className="space-y-3">
            <div className="h-4 w-full bg-zinc-100 rounded-full overflow-hidden flex">
              <div 
                style={{ width: `${laborPct}%` }}
                className="bg-brand h-full transition-all" 
                title={`Mão de Obra: ${laborPct}%`}
              />
              <div 
                style={{ width: `${materialsPct}%` }}
                className="bg-amber-500 h-full transition-all" 
                title={`Materiais: ${materialsPct}%`}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="p-3 bg-emerald-50/50 border border-emerald-100 rounded-2xl flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Wrench className="h-4 w-4 text-brand" />
                  <div>
                    <span className="font-bold text-zinc-900 block">Mão de Obra ({laborPct}%)</span>
                    <span className="text-[10px] text-zinc-500 font-medium">Seu Serviço Técnico</span>
                  </div>
                </div>
                <strong className="text-sm font-black text-brand">{formatCurrencyBRL(totalLaborBilled)}</strong>
              </div>

              <div className="p-3 bg-amber-50/50 border border-amber-100 rounded-2xl flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-amber-600" />
                  <div>
                    <span className="font-bold text-zinc-900 block">Materiais & Peças ({materialsPct}%)</span>
                    <span className="text-[10px] text-zinc-500 font-medium">Insumos Repassados</span>
                  </div>
                </div>
                <strong className="text-sm font-black text-amber-700">{formatCurrencyBRL(totalMaterialsSpent)}</strong>
              </div>
            </div>
          </div>
        </div>

        {/* 4. DRE Tático Visual em Cascata */}
        <div className="bg-white rounded-3xl p-5 sm:p-6 border border-zinc-100 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
            <div>
              <h3 className="text-xs font-black text-zinc-900 uppercase tracking-wider">
                Para Onde Vai o Seu Dinheiro (Passo a Passo)
              </h3>
              <p className="text-[11px] text-zinc-500 font-medium">
                Entenda de forma transparente como cada real faturado é distribuído até sobrar o seu lucro.
              </p>
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-brand bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-100">
              Margem Protegida
            </span>
          </div>

          <div className="space-y-2.5 text-xs">
            {/* Linha 1: Faturamento Bruto */}
            <div className="p-3.5 bg-zinc-50 rounded-2xl border border-zinc-100 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="w-6 h-6 rounded-lg bg-zinc-200 text-zinc-700 flex items-center justify-center font-bold text-xs">1</span>
                <div>
                  <span className="font-bold text-zinc-900 block">(+) Total Faturado no Mês</span>
                  <span className="text-[10px] text-zinc-400 font-medium">Total de propostas comerciais e ordens de serviço concluídas</span>
                </div>
              </div>
              <strong className="text-sm font-black text-zinc-900">{formatCurrencyBRL(grossBilledMonth)}</strong>
            </div>

            {/* Linha 2: Insumos & Materiais */}
            <div className="p-3.5 bg-zinc-50 rounded-2xl border border-zinc-100 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="w-6 h-6 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center font-bold text-xs">2</span>
                <div>
                  <span className="font-bold text-zinc-900 block">(-) Gastos com Insumos, Materiais e Peças</span>
                  <span className="text-[10px] text-zinc-400 font-medium">Custo direto dos produtos e insumos aplicados</span>
                </div>
              </div>
              <strong className="text-sm font-black text-rose-600">- {formatCurrencyBRL(totalMaterialsSpent)}</strong>
            </div>

            {/* Linha 3: Custos Fixos & Despesas */}
            <div className="p-3.5 bg-zinc-50 rounded-2xl border border-zinc-100 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="w-6 h-6 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-xs">3</span>
                <div>
                  <span className="font-bold text-zinc-900 block">(-) Custos Fixos do Negócio</span>
                  <span className="text-[10px] text-zinc-400 font-medium">Aluguel, luz, internet, ferramentas, software</span>
                </div>
              </div>
              <strong className="text-sm font-black text-amber-600">- {formatCurrencyBRL(monthlyFixedCosts)}</strong>
            </div>

            {/* Linha 4: Lucro Líquido Real */}
            <div className="p-4 bg-emerald-50/60 rounded-2xl border border-emerald-200 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="w-6 h-6 rounded-lg bg-brand text-white flex items-center justify-center font-black text-xs">4</span>
                <div>
                  <span className="font-black text-zinc-900 block text-xs sm:text-sm">(=) Lucro Livre Que Sobra no Seu Bolso</span>
                  <span className="text-[10px] text-emerald-800 font-medium">Dinheiro limpo após todas as contas e insumos pagos</span>
                </div>
              </div>
              <div className="text-right">
                <strong className="text-base sm:text-lg font-black text-brand block">{formatCurrencyBRL(netProfitMonth)}</strong>
                <span className="text-[10px] font-bold text-emerald-700">{netMarginPercent.toFixed(1)}% de margem</span>
              </div>
            </div>
          </div>
        </div>

        {/* 5. Ações Rápidas (Atalhos Úteis) */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {onNavigateToCostCenter && (
            <button
              type="button"
              onClick={onNavigateToCostCenter}
              className="p-4 bg-white hover:bg-zinc-50 border border-zinc-100 rounded-2xl text-left transition-all cursor-pointer shadow-xs flex items-center justify-between active:scale-95 group"
            >
              <div>
                <span className="text-[10px] text-zinc-400 font-black uppercase tracking-widest block">Custos & Salário</span>
                <strong className="text-xs font-black text-zinc-900 group-hover:text-brand transition-colors">Revisar Meus Custos</strong>
              </div>
              <ChevronRight className="h-4 w-4 text-zinc-400 group-hover:text-brand" />
            </button>
          )}

          {onNavigateToWorkOrders && (
            <button
              type="button"
              onClick={onNavigateToWorkOrders}
              className="p-4 bg-white hover:bg-zinc-50 border border-zinc-100 rounded-2xl text-left transition-all cursor-pointer shadow-xs flex items-center justify-between active:scale-95 group"
            >
              <div>
                <span className="text-[10px] text-zinc-400 font-black uppercase tracking-widest block">Ordens de Serviço</span>
                <strong className="text-xs font-black text-zinc-900 group-hover:text-brand transition-colors">Minhas Ordens de Serviço</strong>
              </div>
              <ChevronRight className="h-4 w-4 text-zinc-400 group-hover:text-brand" />
            </button>
          )}

          {onNavigateToReceipts && (
            <button
              type="button"
              onClick={onNavigateToReceipts}
              className="p-4 bg-white hover:bg-zinc-50 border border-zinc-100 rounded-2xl text-left transition-all cursor-pointer shadow-xs flex items-center justify-between active:scale-95 group"
            >
              <div>
                <span className="text-[10px] text-zinc-400 font-black uppercase tracking-widest block">Recibos PIX</span>
                <strong className="text-xs font-black text-zinc-900 group-hover:text-brand transition-colors">Meus Recibos Emitidos</strong>
              </div>
              <ChevronRight className="h-4 w-4 text-zinc-400 group-hover:text-brand" />
            </button>
          )}

          {onNavigateToCalendar && (
            <button
              type="button"
              onClick={onNavigateToCalendar}
              className="p-4 bg-white hover:bg-zinc-50 border border-zinc-100 rounded-2xl text-left transition-all cursor-pointer shadow-xs flex items-center justify-between active:scale-95 group"
            >
              <div>
                <span className="text-[10px] text-zinc-400 font-black uppercase tracking-widest block">Agenda</span>
                <strong className="text-xs font-black text-zinc-900 group-hover:text-brand transition-colors">Minha Agenda & Horários</strong>
              </div>
              <ChevronRight className="h-4 w-4 text-zinc-400 group-hover:text-brand" />
            </button>
          )}
        </div>

      </div>
    </div>
  );
}
